import React, { useState, useCallback } from "react";
import Modal from "../Common/Modal";
import BaseButton from "../Common/Buttons/BaseButton";
import SecondaryButton from "../Common/Buttons/SecondaryButton";
import PaymentButton from "../../components/Common/Buttons/PaymentButton";
import { HttpRequest } from "../../utils/common";
import { AddNumberDelimiter } from "../../utils/TextUtil";
import TippingImg from "../../../images/tipping.jpeg";

type Props = {
  projectID: number;
  setInvestmentTotalAmount: (amount: number) => void;
  canTip: boolean;
  isLoggedIn: boolean;
  cardRegistered: boolean;
  accountEmail: string;
  payJpPublicKey: string;
  tippingAmounts?: number[];
};

export const DEFAULT_TIPPING_AMOUNTS = [300, 500, 700, 1_000, 1_500, 2_000];

const Tipping: React.FC<Props> = (props) => {
  const {
    projectID,
    setInvestmentTotalAmount,
    canTip,
    isLoggedIn,
    cardRegistered,
    accountEmail,
    payJpPublicKey,
    tippingAmounts = DEFAULT_TIPPING_AMOUNTS,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isTippingCompleted, setIsTippingCompleted] = useState<boolean>(false);
  const tippingOptions = tippingAmounts.map((amount) => `${amount.toLocaleString()}円`);
  const tippingValues = tippingAmounts.map(String);
  const [tippingAmount, setTippingAmount] = useState<string>(tippingValues[0]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [cardDone, setCardDone] = useState<boolean>(cardRegistered);
  const [cardRegistrationOpenSignal, setCardRegistrationOpenSignal] =
    useState<number>(0);

  const cardRenewalCallback = async (payjp_token: string) => {
    HttpRequest.put("/api/accounts/payment_object_id", { payjp_token })
      .then(async () => {
        setErrorMessage("");
        setCardDone(true);
        setIsModalOpen(true);
      })
      .catch((error) => {
        console.log(error);
        setErrorMessage(
          "カード登録中にエラーが発生しました。入力内容をご確認のうえ、再度お試しください。"
        );
        return;
      });
  };

  const onTipping = useCallback(() => {
    setIsSubmitting(true);
    HttpRequest.post(`/api/investments/${projectID}/tipping`, {
      amount: Number(tippingAmount),
    })
      .then((res) => {
        setIsTippingCompleted(true);
        setInvestmentTotalAmount(res.data.current_funding);
        setIsSubmitting(false);
      })
      .catch((e) => {
        const errors = e.response.data.errors;
        if (errors?.maximum_price) {
          setErrorMessage(errors.maximum_price);
        } else if (errors?.invalid_amount) {
          setErrorMessage(errors.invalid_amount);
        } else if (errors?.ended) {
          setErrorMessage(errors.ended);
        } else if (errors?.card_declined) {
          setErrorMessage(errors.card_declined);
        } else if (errors?.incorrect_card_data) {
          setErrorMessage(errors.incorrect_card_data);
        } else {
          setErrorMessage(
            "投げ銭処理中にエラーが発生しました。再度やり直してもエラーが発生する場合は、お問い合わせよりご連絡ください。"
          );
        }
        setIsTippingCompleted(true);
        setIsSubmitting(false);
      });
  }, [setInvestmentTotalAmount, setIsSubmitting, tippingAmount, projectID]);

  const handleTippinButtonClick = () => {
    if (!canTip) {
      // 無反応でOK
      return;
    }
    if (!isLoggedIn) {
      window.location.assign("/accounts/sign_in");
      return;
    }
    setIsModalOpen(true);
  };

  const openCardRegistrationModal = () => {
    setIsModalOpen(false);
    setCardRegistrationOpenSignal((current) => current + 1);
  };

  return (
    <>
      <div className="h-10">
        <BaseButton
          text="投げ銭で応援する"
          fontSize="text-sm"
          disabled={!canTip}
          onClick={handleTippinButtonClick}
        />
      </div>
      <PaymentButton
        disabled={() => false}
        submitText="カードを登録する"
        text="カードを登録する"
        payJpPublicKey={payJpPublicKey}
        callback={cardRenewalCallback}
        hideConfirmationModal={true}
        isAddressEdited={false}
        postal_code=""
        full_address=""
        accountEmail={accountEmail}
        hideButton={true}
        openSignal={cardRegistrationOpenSignal}
        onModalClose={() => setIsModalOpen(true)}
      />
      <Modal
        isActive={isModalOpen}
        toggleModal={() => setIsModalOpen(false)}
        padding={30}
        showCloseButton={false}
      >
        {isModalOpen && (
          <div className="justify-center bg-white">
            {errorMessage !== "" && (
              <div className="pt-[80px] text-center">{errorMessage}</div>
            )}
            {cardDone ? (
              <>
                {isTippingCompleted ? (
                  <>
                    <div className="text-center">
                      ありがとうございました。
                      <br />
                      {AddNumberDelimiter(tippingAmount)}円を投げ銭しました。
                    </div>
                    <div className="flex items-center justify-center mb-6 mt-3">
                      <img
                        className="w-[200px]"
                        src={TippingImg}
                        alt={"支援完了しました"}
                        width={200}
                        height={150}
                      />
                    </div>
                    <div className="flex items-center justify-center mt-6">
                      <div className="w-[300px] h-[40px]">
                        <SecondaryButton
                          text="閉じる"
                          onClick={() => {
                            setIsModalOpen(false);
                            setIsTippingCompleted(false);
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pt-5 text-center">
                      いくら投げ銭をしますか？
                    </div>
                    <div className="mt-5 text-left">
                      {tippingValues.map((tippingValue, i) => (
                        <label
                          key={tippingValue}
                          className="flex gap-3 items-center py-2 cursor-pointer select-none"
                          onClick={() => setTippingAmount(tippingValue)}
                        >
                          <input
                            type="radio"
                            name="tipping"
                            value={tippingValue}
                            checked={tippingAmount === tippingValue}
                            onChange={(e) => setTippingAmount(e.target.value)}
                            disabled={isSubmitting}
                            className="peer sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className="border-[#006AC9] peer-checked:border-[#B1C2CC] after:content-[''] peer-checked:after:block after:absolute relative after:inset-1 after:hidden inline-block w-5 h-5 after:bg-black border-2 rounded-full after:rounded-full transition-all"
                          />
                          <span className="text-base">{tippingOptions[i]}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 md:flex md:flex-row-reverse md:items-center md:justify-center">
                      <div className="w-[250px] h-[40px] mt-3 mx-auto md:mr-3">
                        <BaseButton
                          text="投げ銭をする"
                          disabled={isSubmitting}
                          fontSize="text-xs"
                          onClick={onTipping}
                        />
                      </div>
                      <div className="w-[250px] h-[40px] mt-3 mx-auto md:mr-3">
                        <SecondaryButton
                          text="やめる"
                          disabled={isSubmitting}
                          onClick={() => setIsModalOpen(false)}
                        />
                      </div>
                    </div>
                    <div className="mt-3 text-xxs">
                      ※投げ銭はご登録されたクレジットカードより決済されます。登録済みのカード情報を確認されたい方は、
                      <a href="/mypage/credit_card" className="underline">
                        こちら
                      </a>{" "}
                      より御確認できます。
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="text-center">
                  ※投げ銭はクレジットカードのご登録が必要です。
                </div>
                <div className="mt-[50px] md:flex md:flex-row-reverse md:items-center md:justify-center">
                  <div className="w-[250px] h-[40px] mt-3 mx-auto">
                    <BaseButton
                      text="カードを登録する"
                      disabled={false}
                      fontSize="text-xs"
                      onClick={openCardRegistrationModal}
                    />
                  </div>
                  <div className="w-[250px] h-[40px] mt-3 mx-auto">
                    <SecondaryButton
                      text="やめる"
                      disabled={isSubmitting}
                      onClick={() => setIsModalOpen(false)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default Tipping;
